import {
  _DeployItem as DeployItem,
  _DeviceItem as DeviceItem,
  _GroupItem as GroupItem,
  _Item as Item,
  _MemberItem as MemberItem,
  _ItemHelp,
} from '@vectronic/desktop-api';
import { dsEnums, webDtos } from '@vectronic/openApi-customer';
import { Translator } from '../helpers/translator';

export type PageItem = ReturnType<typeof ItemHelp.adminPage>[number];
export type SinglesType = MembersType | webDtos.DropOffExtended[];
export type MembersType =
  | webDtos.CollarExtended[]
  | webDtos.AnimalExtended[]
  | webDtos.TrapExtended[];
export type SingleType = MemberType | webDtos.DropOffExtended;
export type MemberType = webDtos.CollarExtended | webDtos.AnimalExtended | webDtos.TrapExtended;
export type _MembersType = MemberType[];
export type ItemType = ReturnType<typeof ItemHelp.allActive>[number];
export {
  _DeployItem as DeployItem,
  _DeviceItem as DeviceItem,
  _GroupItem as GroupItem,
  _Item as Item,
  _ItemToCustomerWebDto as ItemToCustomerWebDto,
  _ItemToDsDto as ItemToDsDto,
  _ItemToUserDto as ItemToUserDto,
  _MemberItem as MemberItem,
  _RegisterItem as RegisterItem,
  _SingleItem as SingleItem,
  _ToAddItemDto as ToAddItemDto,
  _ToDeployPartnerItem as ToDeployPartnerItem,
  _ToGroupItem as ToGroupItem,
  _ToMemberItem as ToMemberItem,
  _ToStringItem as ToStringItem,
  _TreeviewItem as TreeviewItem,
  _ItemToCustomerWebDto as TypeToItem,
} from '@vectronic/desktop-api';

export class ItemHelp extends _ItemHelp {
  static deviceClassWithOwnIdPoolToItem(
    deviceClassWithOwnIdPool: dsEnums.DeviceClassWithOwnIdPool,
  ) {
    switch (deviceClassWithOwnIdPool) {
      case 'COLLAR':
        return Item.COLLAR;
      case 'TRAP_TRANSMITTER':
        return Item.TRAP;
      case 'DROP_OFF':
        return Item.DROP_OFF;
      case 'EXTERNAL_SENSOR':
        return Item.EXTERNAL_SENSOR;
      case 'CAMERA':
        return Item.CAMERA;
      case 'CONFIGURATION_DEVICE':
        return Item.CONFIGURATION_DEVICE;
      case 'DATA_LOGGER':
        return Item.DATA_LOGGER;
      case 'TT4_SENSOR':
        return Item.TT4_SENSOR;
    }
  }

  static itemToDeviceClassWithOwnIdPool(item: Item) {
    switch (item) {
      case Item.COLLAR:
        return 'COLLAR';
      case Item.TRAP:
        return 'TRAP_TRANSMITTER';
      case Item.DROP_OFF:
        return 'DROP_OFF';
      case Item.EXTERNAL_SENSOR:
        return 'EXTERNAL_SENSOR';
      case Item.CAMERA:
        return 'CAMERA';
      case Item.CONFIGURATION_DEVICE:
        return 'CONFIGURATION_DEVICE';
      case Item.DATA_LOGGER:
        return 'DATA_LOGGER';
      case Item.TT4_SENSOR:
        return 'TT4_SENSOR';
    }
  }

  static toDeviceString(target: DeviceItem) {
    return Translator.camelCase(target);
  }

  static toPathString(target: Item) {
    switch (target) {
      case Item.ANIMAL:
      case Item.COLLAR:
      case Item.TRAP:
      case Item.DROP_OFF:
      case Item.EXTERNAL_SENSOR:
      case Item.CAMERA:
      case Item.CONFIGURATION_DEVICE:
      case Item.DATA_LOGGER:
      case Item.TT4_SENSOR:
        return `${target}s`;
      case Item.ANIMAL_GROUP:
        return 'animals/groups';
      case Item.COLLAR_GROUP:
        return 'collars/groups';
      case Item.TRAP_GROUP:
        return 'traps/groups';
    }
  }

  static fromPathString(path: string) {
    switch (path) {
      case 'animals/groups':
        return Item.ANIMAL_GROUP;
      case 'collars/groups':
        return Item.COLLAR_GROUP;
      case 'traps/groups':
        return Item.TRAP_GROUP;
      default:
        throw new Error(`Incorrect path: ${path}`);
    }
  }

  static fromCellAction(key: string) {
    switch (key) {
      case 'animals':
        return Item.ANIMAL;
      case 'collars':
        return Item.COLLAR;
      case 'traps':
        return Item.TRAP;
      case 'collarGroups':
        return Item.COLLAR_GROUP;
      case 'animalGroups':
        return Item.ANIMAL_GROUP;
      case 'trapGroups':
        return Item.TRAP_GROUP;
      default:
        throw new Error(`Incorrect cell action: ${key}`);
    }
  }

  static toDevicePath(target: DeviceItem) {
    switch (target) {
      case Item.COLLAR:
      case Item.TRAP:
      case Item.CAMERA:
        return `${target}s`;
      case Item.DROP_OFF:
        return `drop-offs`;
      case Item.EXTERNAL_SENSOR:
        return `external-sensors`;
      case Item.CONFIGURATION_DEVICE:
        return `configuration-devices`;
      case Item.DATA_LOGGER:
        return `data-loggers`;
      case Item.TT4_SENSOR:
        return `tt4-sensors`;
    }
  }

  static confirmDeleteString(target: GroupItem) {
    switch (target) {
      case Item.ANIMAL_GROUP:
        return 'animal from group';
      case Item.COLLAR_GROUP:
        return 'collar from group';
      case Item.TRAP_GROUP:
        return 'trap from group';
    }
  }

  static toDeployKey(target: DeployItem) {
    switch (target) {
      case Item.ANIMAL:
        return 'animalId';
      case Item.COLLAR:
        return 'collarId';
    }
  }

  static toDeployPartner(target: DeployItem) {
    switch (target) {
      case Item.ANIMAL:
        return Item.COLLAR;
      case Item.COLLAR:
        return Item.ANIMAL;
    }
  }

  static toRoute(target: Item, id?: number) {
    let route = `/${this.toPathString(target)}`;
    if (id) route += `/${id}`;
    return route;
  }

  static toSelectionPathString(target: GroupItem) {
    switch (target) {
      case Item.ANIMAL_GROUP:
        return 'animal-group';
      case Item.COLLAR_GROUP:
        return 'collar-group';
      case Item.TRAP_GROUP:
        return 'trap-group';
    }
  }

  static toDialogString(target: Item) {
    switch (target) {
      case Item.ANIMAL_GROUP:
        return 'animal group';
      case Item.COLLAR_GROUP:
        return 'collar group';
      case Item.TRAP_GROUP:
        return 'trap group';
      default:
        return target;
    }
  }

  static toLabel(target: MemberItem | GroupItem) {
    switch (target) {
      case Item.ANIMAL:
        return 'Animals';
      case Item.COLLAR:
        return 'Collars';
      case Item.TRAP:
        return 'Traps';
      case Item.ANIMAL_GROUP:
        return 'Animal Groups';
      case Item.COLLAR_GROUP:
        return 'Collar Groups';
      case Item.TRAP_GROUP:
        return 'Trap Groups';
    }
  }

  static toTitle(target: Item) {
    switch (target) {
      case Item.ANIMAL:
      case Item.COLLAR:
      case Item.TRAP:
      case Item.ANIMAL_GROUP:
      case Item.COLLAR_GROUP:
      case Item.TRAP_GROUP:
        return this.toLabel(target);
      case Item.DROP_OFF:
        return 'Drop Offs';
      default:
        return '';
    }
  }

  static fromActionName(name: string) {
    for (const item of [...this.member(), ...this.group()]) {
      const label = this.toLabel(item);
      if (name.includes(label)) return item;
    }
    return undefined;
  }

  static toMatIcon(target: MemberItem | GroupItem) {
    switch (target) {
      case Item.ANIMAL:
        return 'pets';
      case Item.COLLAR:
        return 'circle';
      case Item.TRAP:
        return 'view_week';
      case Item.ANIMAL_GROUP:
      case Item.COLLAR_GROUP:
      case Item.TRAP_GROUP:
        return 'groups';
    }
  }

  static allActive(): ReturnType<typeof ItemHelp.all> {
    return [...this.member(), ...this.group(), Item.DROP_OFF, Item.CAMERA] as any;
  }

  static activeSingle() {
    return [...this.member(), Item.DROP_OFF] as const; //NOTE currently only used for routing, camera not needed
  }

  static adminPage() {
    return [Item.COLLAR, Item.EXTERNAL_SENSOR, Item.CAMERA, Item.DROP_OFF] as const;
  }
}
